
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas, Card, Accordion } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import staticModules from '../../constants/modules';
import Swal from 'sweetalert2';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = `${backendUrl}/api/roles`;

const RoleDetails = () => {
    const [roles, setRoles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        data_scope: 'self',
        parent_id: '',
        permissions: [],
        modules: []
    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [permissions, setPermissions] = useState([]);  // For storing available permissions
    const [selectedPermissions, setSelectedPermissions] = useState([]); // For selected permissions
    const didFetchRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [permissionsGrouped, setPermissionsGrouped] = useState({});
    const { updatePermissions } = useAuth();
    const WithCreatePermission = withPermission(({ children }) => children, ['role-create']);
    const WithEditPermission = withPermission(({ children }) => children, ['role-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['role-delete']);
    const WithAssignPermission = withPermission(({ children }) => children, ['permission-assign']);
    const WithModulePermission = withPermission(({ children }) => children, ['module-assign']);

    const [searchQuery, setSearchQuery] = useState('');
    const [permissionSearchQuery, setPermissionSearchQuery] = useState('');
    const [selectedModuleTab, setSelectedModuleTab] = useState('All');
    const debounceTimeoutRef = useRef(null);
    const [errors, setErrors] = useState({});
    const [modules, setModules] = useState([]); // All modules fetched from API
    const [selectedModules, setSelectedModules] = useState([]); // Selected module IDs
    const [showModuleModal, setShowModuleModal] = useState(false);
    const [showPermissionOffcanvas, setShowPermissionOffcanvas] = useState(false);
    const [permissionRole, setPermissionRole] = useState(null);

    useEffect(() => {
        if (!didFetchRef.current) {
            fetchPermissions();  // Fetch permissions for the Assign Permissions modal
            didFetchRef.current = true;
        }
    }, []);
    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchRoles(1, searchQuery);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);

    };

    useEffect(() => {
        console.log('Permissions updated:', permissions);  // You can check if permissions are updating correctly
    }, [permissions]);

    const fetchRoles = async (page = 1) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;
            const response = await axios.get(`${API_URL}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery,
                },
            });
            const { data, current_page, last_page } = response.data.data;

            setRoles(data);
            setCurrentPage(current_page - 1);
            setPageCount(last_page);
            // setPageCount(response.data.last_page);
            // setCurrentPage(response.data.current_page);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const handleAddRole = () => {
        setFormData({
            name: '',
            data_scope: 'self',
            parent_id: '',
            permissions: [],
            modules: []
        });
        setIsEdit(false);
        setSelectedRole(null);
        setShowModal(true);
        setErrors({});
    };

    const handleEditRole = (role) => {
        setFormData({
            name: role.name,
            data_scope: role.data_scope || 'self',
            parent_id: role.parent_id || '',
            permissions: role.permissions ? role.permissions.map(p => p.id) : [],
            modules: role.modules ? (typeof role.modules === 'string' ? JSON.parse(role.modules) : role.modules) : []
        });
        setIsEdit(true);
        setSelectedRole(role);
        setShowModal(true);
        setErrors({});
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    await axios.delete(`${API_URL}/delete/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
                    setRoles(roles.filter(role => role.id !== id));
                    Swal.fire('Deleted!', 'Role has been deleted.', 'success');
                } catch (error) {
                    console.error('Error deleting role:', error);
                    Swal.fire('Error!', 'Failed to delete role.', 'error');
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: undefined,
        }));
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchRoles(newPage);
    };

    useEffect(() => {
        if (!showModal) {
            setErrors({});
        }
    }, [showModal]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        return newErrors;
    };

    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        const token = userInfo?.token;

        try {
            let response;
            if (isEdit) {
                response = await axios.put(`${API_URL}/${selectedRole.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setRoles(roles.map(ind => (ind.id === selectedRole.id ? response.data.data : ind)));
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Role Updated Successfully',
                    timer: 3000,
                    showConfirmButton: false,
                });
            } else {
                response = await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setRoles([...roles, response.data.data]);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Role Created Successfully',
                    timer: 3000,
                    showConfirmButton: false,
                });
            }
            setShowModal(false);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.errors) {
                setErrors(error.response.data.errors);
            } else {
                console.error('Unexpected error: ', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAssignPermission = (role) => {
        setPermissionRole(role);
        setFormData({
            ...formData,
            permissions: role.permissions ? role.permissions.map(p => p.id) : [],
            modules: role.modules ? (typeof role.modules === 'string' ? JSON.parse(role.modules) : role.modules) : []
        });
        setShowPermissionOffcanvas(true);
    };

    const handlePermissionSubmit = async () => {
        setLoading(true);
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        const token = userInfo?.token;

        try {
            // Assign Permissions
            await axios.post(`${backendUrl}/api/roles/${permissionRole.id}/permissions`, {
                permissions: formData.permissions
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            // Assign Modules
            await axios.post(`${backendUrl}/api/roles/${permissionRole.id}/modules`, {
                modules: formData.modules
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Permissions and Modules Updated',
                timer: 3000,
                showConfirmButton: false,
            });

            setShowPermissionOffcanvas(false);
            fetchRoles(currentPage + 1);
        } catch (error) {
            console.error('Error assigning permissions/modules:', error);
            Swal.fire('Error!', 'Failed to update permissions/modules.', 'error');
        } finally {
            setLoading(false);
        }
    };


    const fetchPermissions = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.get(`${backendUrl}/api/permissions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            const grouped = groupPermissionsByParent(response.data.data);
            setPermissions(response.data.data);
            setPermissionsGrouped(grouped);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const groupPermissionsByParent = (permissions) => {
        return permissions.reduce((acc, permission) => {
            const parentName = permission.parent_name || 'No Parent';
            if (!acc[parentName]) {
                acc[parentName] = [];
            }
            acc[parentName].push(permission);
            return acc;
        }, {});
    };

    const handlePermissionChange = (permissionId) => {
        setFormData(prev => {
            const updatedPermissions = prev.permissions.includes(permissionId)
                ? prev.permissions.filter(id => id !== permissionId)
                : [...prev.permissions, permissionId];
            return { ...prev, permissions: updatedPermissions };
        });
    };

    const handleModuleChange = (moduleId) => {
        setFormData(prev => {
            const updatedModules = prev.modules.includes(moduleId)
                ? prev.modules.filter(id => id !== moduleId)
                : [...prev.modules, moduleId];
            return { ...prev, modules: updatedModules };
        });
    };

    const modalRef = useRef(null);

    const capitalizeFirstLetter = (string) => {
        if (!string) return "";
        const mapping = {
            'licensetype': 'License Type',
            'productpricing': 'Product Pricing',
            'systemtenant': 'System Tenant',
            'amcsetting': 'AMC Setting',
            'proformainvoice': 'Proforma Invoice',
            'taxinvoice': 'Tax Invoice',
            'purchaseorder': 'Purchase Order',
            'creditnote': 'Credit Note',
            'vatpayment': 'VAT Payment',
            'vatreturn': 'VAT Return',
            'accountsummary': 'Account Summary',
            'employeedoc': 'Employee Document',
            'employeecontract': 'Employee Contract',
            'leavetype': 'Leave Type',
            'leaveapply': 'Leave Apply',
            'overtimetype': 'Overtime Type'
        };
        if (mapping[string.toLowerCase()]) return mapping[string.toLowerCase()];
        return string.charAt(0).toUpperCase() + string.slice(1).replace(/-/g, ' ');
    };

    useEffect(() => {
        setModules(staticModules);
    }, []);

    const modulePermissionMapping = {
        'Sales': ['organisation', 'deal', 'lead', 'quotation', 'purchaseorder', 'creditnote', 'sales', 'order', 'activity', 'activitylog', 'activitytype', 'quotationhistory', 'quotationterm'],
        'Operation': ['client', 'project', 'proformainvoice', 'taxinvoice', 'expense', 'contact', 'vendor', 'projectassignment', 'recognizedexpense', 'revenuerecognition'],
        'License Management': ['instance', 'license', 'tenant', 'document', 'licenseextension', 'subscriptionalert', 'vendorsubscription'],
        'Product': ['brand', 'licensetype', 'product', 'productpricing'],
        'User Manager': ['user', 'role', 'permission', 'module', 'systemtenant'],
        'Setting': ['setting', 'prefix', 'country', 'industry', 'accounting', 'lock', 'numbersequence', 'financialperiod', 'mailsetting', 'amcsetting'],
        'Contact': ['contact', 'contacttype', 'communication', 'clientcontact'],
        'Account': ['bankaccount', 'transaction', 'expensetype', 'vatpayment', 'vatreturn', 'accountsummary', 'category', 'bankexchangerate', 'reconciliation', 'customercreditbalance', 'billtransaction'],
        'Dashboard': ['dashboard'],
        'Reports': ['reports', 'balancesheet', 'profitloss', 'soa', 'revenue', 'bankreport'],
        'HR': ['employee', 'employeedoc', 'employeecontract', 'allowance', 'attendance', 'leavetype', 'leave', 'leaveapply', 'holiday', 'event', 'loan', 'deduction', 'overtimetype', 'overtime', 'payroll', 'payrollprovider', 'leaveentitlement', 'leaveapplication', 'salarystructure', 'payslip'],
    };

    const getOtherPrefixes = () => {
        const allMappedPrefixes = Object.values(modulePermissionMapping).flat();
        return Object.keys(permissionsGrouped).filter(prefix =>
            prefix !== 'No Parent' && !allMappedPrefixes.includes(prefix.toLowerCase())
        );
    };

    const otherPrefixes = getOtherPrefixes().filter(prefix => {
        if (!permissionSearchQuery) return true;
        const query = permissionSearchQuery.toLowerCase();
        return prefix.toLowerCase().includes(query) ||
            (permissionsGrouped[prefix]?.some(p => p.name.toLowerCase().includes(query)));
    });

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Role</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item"><a href="javascript:void(0);">Role</a></li>
                                        <li className="breadcrumb-item active">Role List</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control  font-size-13"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>
                                        </div>
                                        <WithCreatePermission>
                                            <button
                                                type="button"
                                                className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                onClick={handleAddRole} data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Add Role"
                                            >
                                                <i className="mdi mdi-plus"></i>
                                            </button></WithCreatePermission>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-nowrap table-check table-list">
                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th style={{ width: '20px' }} className="align-middle">
                                                        <div className="form-check font-size-16">
                                                            <input className="form-check-input" type="checkbox" id="checkAll" />
                                                            <label className="form-check-label" htmlFor="checkAll"></label>
                                                        </div>
                                                    </th>
                                                    <th className="align-middle">#</th>
                                                    <th className="align-middle">Name</th>
                                                    <th className="align-middle">Data Access Scope</th>
                                                    <th className="align-middle">Parent Role</th>
                                                    <th className="align-middle">Created On</th>
                                                    <th className="align-middle">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[...roles]
                                                    .sort((a, b) => a.name.localeCompare(b.name))
                                                    .map((role, index) => (
                                                        <tr key={role.id}>
                                                            <td>
                                                                <div className="form-check font-size-16">
                                                                    <input className="form-check-input" type="checkbox" />
                                                                </div>
                                                            </td>
                                                            <td>{index + 1}</td>
                                                            <td>{role.name}</td>
                                                            <td>{role.data_scope ? capitalizeFirstLetter(role.data_scope) : 'Self'}</td>
                                                            <td>{role.parent ? capitalizeFirstLetter(role.parent.name) : '-'}</td>
                                                            <td>{new Date(role.created_at).toLocaleDateString()}</td>
                                                            <td>
                                                                <div className="dropdown">
                                                                    <i
                                                                        className="mdi mdi-dots-horizontal font-size-18"
                                                                        data-bs-toggle="dropdown"
                                                                        aria-expanded="false"
                                                                        style={{ cursor: 'pointer' }}
                                                                    ></i>
                                                                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                        <li><WithEditPermission>
                                                                            <button
                                                                                className="dropdown-item"
                                                                                onClick={() => handleEditRole(role)}
                                                                            >
                                                                                Edit
                                                                            </button></WithEditPermission>
                                                                        </li>
                                                                        <li><WithDeletePermission>
                                                                            <button
                                                                                className="dropdown-item text-danger"
                                                                                onClick={() => handleDelete(role.id)}
                                                                            >
                                                                                Delete
                                                                            </button></WithDeletePermission>
                                                                        </li>
                                                                        <li>
                                                                            <WithAssignPermission>
                                                                                <button
                                                                                    className="dropdown-item"
                                                                                    onClick={() => handleAssignPermission(role)}
                                                                                >
                                                                                    Assign Permission/Module
                                                                                </button>
                                                                            </WithAssignPermission>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-end pagination-sm">
                                        <ReactPaginate
                                            previousLabel={'<'}
                                            nextLabel={'>'}
                                            breakLabel={'...'}
                                            pageCount={pageCount}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageChange}
                                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                            pageClassName={'page-item'}
                                            pageLinkClassName={'page-link'}
                                            previousClassName={'page-item previous'}
                                            previousLinkClassName={'page-link'}
                                            nextClassName={'page-item next'}
                                            nextLinkClassName={'page-link'}
                                            breakClassName={'page-item'}
                                            breakLinkClassName={'page-link'}
                                            activeClassName={'active'}
                                        />
                                    </div>

                                    {/* Consolidated Role Modal */}
                                    <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement="end" style={{ width: '500px' }} className="custom-offcanvas">
                                        <Offcanvas.Header closeButton>
                                            <Offcanvas.Title className="fw-bold text-primary">{isEdit ? 'Edit Role' : 'Add Role'}</Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <hr style={{ margin: 0 }} />
                                        <Offcanvas.Body>
                                            <Form>
                                                <div className="row">
                                                    <div className="col-md-12 mb-3">
                                                        <Form.Group controlId="roleName">
                                                            <Form.Label className="fw-bold">Role Name</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="name"
                                                                value={formData.name}
                                                                onChange={handleInputChange}
                                                                className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                                                                placeholder="Enter role name"
                                                            />
                                                            {errors.name && <div className="invalid-feedback">*{errors.name}</div>}
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <Form.Group controlId="dataScope">
                                                            <Form.Label className="fw-bold">Data Access Scope</Form.Label>
                                                            <Form.Select
                                                                name="data_scope"
                                                                value={formData.data_scope}
                                                                onChange={handleInputChange}
                                                            >
                                                                <option value="self">Own Data</option>
                                                                <option value="team">Team Data</option>
                                                                <option value="all">All Data</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <Form.Group controlId="parentId">
                                                            <Form.Label className="fw-bold">Parent Role</Form.Label>
                                                            <Form.Select
                                                                name="parent_id"
                                                                value={formData.parent_id}
                                                                onChange={handleInputChange}
                                                            >
                                                                <option value="">No Parent</option>
                                                                {roles.filter(r => r.id !== selectedRole?.id).map(role => (
                                                                    <option key={role.id} value={role.id}>{capitalizeFirstLetter(role.name)}</option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </div>
                                                </div>
                                            </Form>
                                        </Offcanvas.Body>
                                        <div className="p-3 d-flex justify-content-end border-top bg-light">
                                            <Button variant="secondary me-2" onClick={() => setShowModal(false)} disabled={loading}>
                                                Close
                                            </Button>
                                            <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                                                {loading ? <i className="bx bx-loader bx-spin me-2"></i> : null}
                                                {isEdit ? 'Update Role' : 'Create Role'}
                                            </Button>
                                        </div>
                                    </Offcanvas>

                                    {/* Permission & Module Assignment Offcanvas */}
                                    <Offcanvas show={showPermissionOffcanvas} onHide={() => setShowPermissionOffcanvas(false)} placement="end" style={{ width: '700px' }} className="custom-offcanvas">
                                        <Offcanvas.Header closeButton>
                                            <Offcanvas.Title className="fw-bold text-primary">Assign Permissions & Modules - {permissionRole?.name}</Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <hr style={{ margin: 0 }} />
                                        <Offcanvas.Body>
                                            <style>
                                                {`
                                                    .custom-offcanvas .form-check-input {
                                                        border: 2px solid #adb5bd !important;
                                                        cursor: pointer;
                                                    }
                                                    .custom-offcanvas .form-check-input:checked {
                                                        background-color: #556ee6 !important;
                                                        border-color: #556ee6 !important;
                                                    }
                                                    .role-section-title {
                                                        font-size: 15px;
                                                        font-weight: 600;
                                                        color: #495057;
                                                        margin-bottom: 15px;
                                                        padding-bottom: 5px;
                                                        border-bottom: 2px solid #eff2f7;
                                                    }
                                                    .accordion-button:not(.collapsed) {
                                                        background-color: #f8f9fa;
                                                        color: #556ee6;
                                                    }
                                                    .permission-group-header {
                                                        background-color: #f8f9fa;
                                                        border-radius: 6px;
                                                        transition: all 0.2s;
                                                    }
                                                    .permission-group-header:hover {
                                                        background-color: #f1f3f5;
                                                    }
                                                    .permission-item {
                                                        border-radius: 4px;
                                                        padding: 2px 5px;
                                                        transition: background 0.2s;
                                                    }
                                                    .permission-item:hover {
                                                        background: #fdfdfd;
                                                    }
                                                    .search-container {
                                                        position: sticky;
                                                        top: -1px;
                                                        background: white;
                                                        z-index: 10;
                                                        padding-bottom: 15px;
                                                        margin-bottom: 15px;
                                                        border-bottom: 1px solid #eff2f7;
                                                    }
                                                `}
                                            </style>
                                            <Form>
                                                <div className="search-container mt-2">
                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-lg font-size-13 shadow-sm border-light"
                                                            placeholder="Search modules or permissions..."
                                                            style={{ paddingLeft: '40px', borderRadius: '10px' }}
                                                            value={permissionSearchQuery}
                                                            onChange={(e) => setPermissionSearchQuery(e.target.value)}
                                                        />
                                                        <i className="bx bx-search-alt position-absolute" style={{ left: '15px', top: '14px', fontSize: '18px', color: '#adb5bd' }} />
                                                    </div>
                                                </div>

                                                <div className="mt-2">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h5 className="role-section-title mb-0 border-0"><i className="bx bx-layer me-2 text-primary"></i>Module & Permissions</h5>
                                                    </div>

                                                    <Accordion alwaysOpen flush className="border rounded">
                                                        {staticModules.filter(module => {
                                                            if (!permissionSearchQuery) return true;
                                                            const query = permissionSearchQuery.toLowerCase();
                                                            const moduleMatch = module.name.toLowerCase().includes(query);
                                                            const relatedPrefixes = modulePermissionMapping[module.name] || [];
                                                            const permissionMatch = relatedPrefixes.some(prefix =>
                                                                prefix.toLowerCase().includes(query) ||
                                                                (permissionsGrouped[prefix]?.some(p => p.name.toLowerCase().includes(query)))
                                                            );
                                                            return moduleMatch || permissionMatch;
                                                        }).sort((a, b) => a.name.localeCompare(b.name)).map((module, mIndex) => {
                                                            const relatedPrefixes = modulePermissionMapping[module.name] || [];
                                                            const isModuleSelected = formData.modules.includes(module.id);

                                                            return (
                                                                <Accordion.Item eventKey={mIndex.toString()} key={module.id} className="border mb-2 rounded">
                                                                    <Accordion.Header>
                                                                        <div className="d-flex align-items-center w-100 justify-content-between pe-3">
                                                                            <div className="d-flex align-items-center">
                                                                                <Form.Check
                                                                                    type="checkbox"
                                                                                    className="me-3"
                                                                                    checked={isModuleSelected}
                                                                                    onChange={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleModuleChange(module.id);
                                                                                    }}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <span className="fw-semibold">{module.name}</span>
                                                                            </div>
                                                                        </div>
                                                                    </Accordion.Header>
                                                                    <Accordion.Body className="pt-0">
                                                                        <div className="row g-3">
                                                                            {relatedPrefixes.filter(prefix => {
                                                                                if (!permissionSearchQuery) return true;
                                                                                const query = permissionSearchQuery.toLowerCase();
                                                                                return prefix.toLowerCase().includes(query) ||
                                                                                    (permissionsGrouped[prefix]?.some(p => p.name.toLowerCase().includes(query)));
                                                                            }).map(prefix => {
                                                                                const group = permissionsGrouped[prefix];
                                                                                if (!group) return null;

                                                                                const isGroupSelected = group.every(p => formData.permissions.includes(p.id));

                                                                                return (
                                                                                    <div className="col-12" key={prefix}>
                                                                                        <div className="permission-group-header p-2 mb-2 d-flex justify-content-between align-items-center border-bottom">
                                                                                            <span className="fw-bold text-dark font-size-13">
                                                                                                <i className="bx bx-chevron-right me-1 text-primary"></i>
                                                                                                {capitalizeFirstLetter(prefix)}
                                                                                            </span>
                                                                                            <Form.Check
                                                                                                type="checkbox"
                                                                                                label={<span className="font-size-12 fw-medium text-primary">Select All</span>}
                                                                                                checked={isGroupSelected}
                                                                                                onChange={() => {
                                                                                                    const ids = group.map(p => p.id);
                                                                                                    setFormData(prev => {
                                                                                                        let newPerms = [...prev.permissions];
                                                                                                        if (isGroupSelected) {
                                                                                                            newPerms = newPerms.filter(id => !ids.includes(id));
                                                                                                        } else {
                                                                                                            newPerms = [...new Set([...newPerms, ...ids])];
                                                                                                        }
                                                                                                        return { ...prev, permissions: newPerms };
                                                                                                    });
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                        <div className="row ms-2">
                                                                                            {group.filter(p => !permissionSearchQuery || p.name.toLowerCase().includes(permissionSearchQuery.toLowerCase())).map(permission => (
                                                                                                <div className="col-md-4 col-sm-6 mb-2 permission-item" key={permission.id}>
                                                                                                    <Form.Check
                                                                                                        type="checkbox"
                                                                                                        label={<span className="font-size-12 text-muted">{capitalizeFirstLetter(permission.name.split('-').slice(1).join('-'))}</span>}
                                                                                                        checked={formData.permissions.includes(permission.id)}
                                                                                                        onChange={() => handlePermissionChange(permission.id)}
                                                                                                    />
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </Accordion.Body>
                                                                </Accordion.Item>
                                                            );
                                                        })}

                                                        {/* Others Section for unmapped permissions */}
                                                        {otherPrefixes.length > 0 && (
                                                            <Accordion.Item eventKey="others" className="border mb-2 rounded">
                                                                <Accordion.Header>
                                                                    <div className="d-flex align-items-center">
                                                                        <i className="bx bx-plus-circle me-3 text-warning"></i>
                                                                        <span className="fw-semibold">Other Permissions</span>
                                                                    </div>
                                                                </Accordion.Header>
                                                                <Accordion.Body className="pt-0">
                                                                    <div className="row g-3">
                                                                        {otherPrefixes.map(prefix => {
                                                                            const group = permissionsGrouped[prefix];
                                                                            if (!group) return null;
                                                                            const isGroupSelected = group.every(p => formData.permissions.includes(p.id));
                                                                            return (
                                                                                <div className="col-12" key={prefix}>
                                                                                    <div className="permission-group-header p-2 mb-2 d-flex justify-content-between align-items-center border-bottom">
                                                                                        <span className="fw-bold text-dark font-size-13">{capitalizeFirstLetter(prefix)}</span>
                                                                                        <Form.Check
                                                                                            type="checkbox"
                                                                                            label={<span className="font-size-12 fw-medium text-primary">Select All</span>}
                                                                                            checked={isGroupSelected}
                                                                                            onChange={() => {
                                                                                                const ids = group.map(p => p.id);
                                                                                                setFormData(prev => {
                                                                                                    let newPerms = [...prev.permissions];
                                                                                                    if (isGroupSelected) {
                                                                                                        newPerms = newPerms.filter(id => !ids.includes(id));
                                                                                                    } else {
                                                                                                        newPerms = [...new Set([...newPerms, ...ids])];
                                                                                                    }
                                                                                                    return { ...prev, permissions: newPerms };
                                                                                                });
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    <div className="row ms-2">
                                                                                        {group.map(permission => (
                                                                                            <div className="col-md-4 col-sm-6 mb-2 permission-item" key={permission.id}>
                                                                                                <Form.Check
                                                                                                    type="checkbox"
                                                                                                    label={<span className="font-size-12 text-muted">{capitalizeFirstLetter(permission.name.split('-').slice(1).join('-'))}</span>}
                                                                                                    checked={formData.permissions.includes(permission.id)}
                                                                                                    onChange={() => handlePermissionChange(permission.id)}
                                                                                                />
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        )}
                                                    </Accordion>
                                                </div>
                                            </Form>
                                        </Offcanvas.Body>
                                        <div className="p-3 d-flex justify-content-end border-top bg-light">
                                            <Button variant="secondary me-2" onClick={() => setShowPermissionOffcanvas(false)} disabled={loading}>
                                                Close
                                            </Button>
                                            <Button variant="primary" onClick={handlePermissionSubmit} disabled={loading}>
                                                {loading ? <i className="bx bx-loader bx-spin me-2"></i> : null}
                                                Save Changes
                                            </Button>
                                        </div>
                                    </Offcanvas>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};




export default RoleDetails;




